import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, PartyPopper, Check, UserX, ChevronDown, X } from "lucide-react";

const inputCls =
  "w-full border-2 border-retro-ink bg-white px-4 py-3 text-lg font-medium text-retro-ink outline-none transition-colors focus:border-retro-orange placeholder:text-retro-ink/40";

export const RsvpForm = ({ items, onReloadItems, onSubmit }) => {
  const [name, setName] = useState("");
  const [guestCount, setGuestCount] = useState(1);
  const [selected, setSelected] = useState([]); // array of item ids
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const itemById = (id) => items.find((i) => i.id === id);
  const isFull = (it) => it.claim_count >= it.max_claims;

  const toggleItem = (it) => {
    if (isFull(it)) return;
    setSelected((prev) =>
      prev.includes(it.id) ? prev.filter((x) => x !== it.id) : [...prev, it.id]
    );
    setErrors((e) => ({ ...e, items: undefined }));
  };

  const removeItem = (id) => setSelected((prev) => prev.filter((x) => x !== id));

  const handleJustMyself = () => {
    toast.error("Nice try. You have to contribute ;)");
  };

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = "We need your name!";
    if (!guestCount || guestCount < 1) e.guestCount = "At least 1 guest.";
    if (selected.length === 0) e.items = "Pick at least one thing to bring.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await onSubmit({
        name: name.trim(),
        guest_count: Number(guestCount),
        item_ids: selected,
      });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Something went wrong. Try again.";
      toast.error(detail);
      if (status === 409) {
        setSelected([]);
        await onReloadItems();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderFieldContent = () => {
    if (selected.length === 0) {
      return <span className="text-retro-ink/40">Select items to bring</span>;
    }
    if (selected.length <= 3) {
      return (
        <span className="flex flex-wrap gap-1.5">
          {selected.map((id) => {
            const it = itemById(id);
            if (!it) return null;
            return (
              <span
                key={id}
                data-testid={`chip-${id}`}
                className="inline-flex items-center gap-1 border-2 border-retro-ink bg-retro-orange px-2 py-0.5 text-sm font-semibold text-white"
              >
                {it.name}
                <span
                  role="button"
                  tabIndex={0}
                  data-testid={`chip-remove-${id}`}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    removeItem(id);
                  }}
                  className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full hover:bg-white/25"
                  aria-label={`Remove ${it.name}`}
                >
                  <X className="h-3 w-3" strokeWidth={3} />
                </span>
              </span>
            );
          })}
        </span>
      );
    }
    const names = selected.map((id) => itemById(id)?.name).filter(Boolean);
    return (
      <span className="font-semibold text-retro-ink">
        {names.slice(0, 2).join(", ")} <span className="text-retro-ink/60">+{names.length - 2} more</span>
      </span>
    );
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="rsvp-form"
      className="space-y-6 border-2 border-retro-ink bg-white p-6 shadow-retro-xl sm:p-8"
    >
      <div>
        <label htmlFor="name" className="mb-2 block font-chango text-lg text-retro-ink">
          Your name
        </label>
        <input
          id="name"
          data-testid="rsvp-name-input"
          className={inputCls}
          placeholder="e.g. Sandy Shores"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        {errors.name && (
          <p className="mt-1 text-sm font-semibold text-retro-orange" data-testid="name-error">{errors.name}</p>
        )}
      </div>

      <div>
        <label htmlFor="guests" className="mb-2 block font-chango text-lg text-retro-ink">
          How many coming (including you)?
        </label>
        <input
          id="guests"
          type="number"
          min="1"
          data-testid="rsvp-guest-count-input"
          className={inputCls}
          value={guestCount}
          onChange={(e) => setGuestCount(e.target.value)}
        />
        {errors.guestCount && (
          <p className="mt-1 text-sm font-semibold text-retro-orange" data-testid="guest-count-error">{errors.guestCount}</p>
        )}
      </div>

      <div>
        <label className="mb-2 block font-chango text-lg text-retro-ink">
          What will you bring?
        </label>

        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            data-testid="items-dropdown-trigger"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            className={`flex min-h-[56px] w-full items-center justify-between gap-2 border-2 bg-white px-4 py-3 text-left text-lg outline-none transition-colors ${
              open ? "border-retro-orange" : "border-retro-ink"
            }`}
          >
            <span className="flex-1">{renderFieldContent()}</span>
            <ChevronDown className={`h-5 w-5 shrink-0 text-retro-ink transition-transform ${open ? "rotate-180" : ""}`} />
          </button>

          {open && (
            <div
              data-testid="items-dropdown-panel"
              className="absolute left-0 right-0 top-[calc(100%+6px)] z-30 max-h-[300px] overflow-y-auto border-2 border-retro-ink bg-white shadow-retro-lg"
            >
              <button
                type="button"
                onClick={handleJustMyself}
                data-testid="just-myself-toggle"
                className="flex w-full items-center gap-3 border-b-2 border-dashed border-retro-ink/40 bg-retro-cream px-4 py-3 text-left font-semibold text-retro-ink/70 transition-colors hover:bg-retro-cream/60"
              >
                <UserX className="h-5 w-5" />
                I'll just bring myself
              </button>

              {items.map((it) => {
                const full = isFull(it);
                const isSel = selected.includes(it.id);
                const left = it.max_claims - it.claim_count;
                return (
                  <button
                    type="button"
                    key={it.id}
                    disabled={full}
                    onClick={() => toggleItem(it)}
                    data-testid={`item-option-${it.id}`}
                    aria-pressed={isSel}
                    className={`flex w-full items-center justify-between gap-3 border-b border-retro-ink/10 px-4 py-3 text-left font-medium transition-colors ${
                      full
                        ? "cursor-not-allowed bg-retro-ink/5 text-retro-ink/35"
                        : isSel
                        ? "bg-retro-orange/10 text-retro-ink hover:bg-retro-orange/15"
                        : "text-retro-ink hover:bg-retro-cream"
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center border-2 ${
                          full
                            ? "border-retro-ink/30 bg-transparent"
                            : isSel
                            ? "border-retro-ink bg-retro-orange"
                            : "border-retro-ink bg-white"
                        }`}
                        aria-hidden="true"
                      >
                        {isSel && !full && <Check className="h-3.5 w-3.5 text-white" strokeWidth={4} />}
                      </span>
                      {it.name}
                    </span>
                    <span className="shrink-0 text-xs font-chango" data-testid={`item-left-${it.id}`}>
                      {full ? "all claimed" : `${left} left`}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {errors.items && (
          <p className="mt-2 text-sm font-semibold text-retro-orange" data-testid="items-error">{errors.items}</p>
        )}
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="rsvp-submit-button"
        className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-retro-ink bg-retro-orange px-6 py-4 font-chango text-xl text-white shadow-retro-lg transition-[transform,box-shadow] duration-150 hover:translate-x-[3px] hover:translate-y-[3px] hover:bg-retro-orangeDark hover:shadow-retro-sm disabled:opacity-70"
      >
        {submitting ? <Loader2 className="h-6 w-6 animate-spin" /> : <PartyPopper className="h-6 w-6" />}
        {submitting ? "Splashing in..." : "Count me in"}
      </button>
    </form>
  );
};
